/**
 * Tech Performance self-serve onboarding: shared constants.
 *
 * Pure values only, so both the wizard (client) and the server modules can import this file.
 * Design: docs/TECH_PERFORMANCE_ONBOARDING.md
 */

// The plan itself is matched by Stripe price id: isTechPerformancePlan / techPerformancePlanIds in lib/plan.ts.

/** Stripe subscription metadata `source`, used by the Stripe webhook to recognise this plan. */
export const TECH_SUBSCRIPTION_SOURCE = "tech-performance-onboarding";

/** Value stored on `User.onboardingFunnel` so login can route a funnel user back to the wizard. */
export const TECH_ONBOARDING_FUNNEL = "tech_performance";

/** The money-back guarantee window, counted from the first successful payment. */
export const REFUND_WINDOW_DAYS = 30;

/** How many days of history the first import pulls. */
export const DATA_PULL_DAYS = 7;

/** A pull with no progress for this long is reported as stalled, so the user can retry. */
export const DATA_PULL_STALL_MS = 10 * 60 * 1000;

/**
 * ServiceTitan imports one day at a time and reports only when a day finishes, and a busy client's
 * day can take a while, so its import is judged on the ServiceTitan job's own last update: stalled
 * after 45 minutes without one, given up (and cancelled) after 3 hours.
 */
export const ST_PULL_STALL_MS = 45 * 60 * 1000;
export const ST_PULL_ABANDON_MS = 3 * 60 * 60 * 1000;

/**
 * Public routes of the funnel. Everything under the prefix is in proxy PUBLIC_PATHS.
 * /tech-boxscore is the wizard: shown when the visitor arrives from checkout (?session_id) or is a
 * signed-in Tech Performance customer; anyone else is sent to log in. There is no landing page; the
 * marketing site links straight to the Stripe payment link. /tech-boxscore/start redirects here
 * for links already sent.
 */
export const TECH_WIZARD_PATH = "/tech-boxscore";

/**
 * Shown only when the plan's real name cannot be read. The real name is the Stripe product behind
 * STRIPE_TECH_PERFORMANCE_PRICE_ID (getTechPlanName), the same name on the customer's receipts.
 */
export const TECH_PLAN_FALLBACK_NAME = "Tech BoxScore";
export const TECH_GUIDE_PATH = "/tech-boxscore/guide";

/**
 * The Tech BoxScore dashboard's page segment (`/{companyId}/<segment>`), where a finished Tech customer
 * lands. Every Tech link (emails, login, home, the wizard, the lock) uses it. The page belongs to the
 * dashboard's own developer: on staging its public address is /{companyId}/tech-boxscore (next.config
 * rewrites it to the tech-performance folder and redirects the old address). Not to be confused with the
 * onboarding at /tech-boxscore (no company id in front).
 */
export const TECH_DASHBOARD_SECTION = "tech-boxscore";
/**
 * Both names, so the Tech plan lock and nav keep the dashboard open at either address. The proxy checks the
 * address before the rewrite, so it sees "tech-boxscore"; old links still arrive as "tech-performance".
 */
export const TECH_DASHBOARD_SECTIONS: readonly string[] = ["tech-performance", "tech-boxscore"];
export function techDashboardPath(companyId: string): string {
  return `/${companyId}/${TECH_DASHBOARD_SECTION}`;
}

/** Wizard progress, in order. A user resumes at the first step they have not finished. */
export const TECH_ONBOARDING_STEPS = [
  "account_created",
  "paid",
  "path_chosen",
  "details_done",
  "crm_connected",
  "data_ready",
] as const;

export type TechOnboardingStep = (typeof TECH_ONBOARDING_STEPS)[number];

/** True when `current` has reached (or passed) `target`. */
export function hasReachedStep(current: TechOnboardingStep | null | undefined, target: TechOnboardingStep) {
  if (!current) return false;
  return TECH_ONBOARDING_STEPS.indexOf(current) >= TECH_ONBOARDING_STEPS.indexOf(target);
}

/** The two CRMs this product supports. */
export const TECH_SUPPORTED_CRMS = ["housecallpro", "servicetitan"] as const;
export type TechCrm = (typeof TECH_SUPPORTED_CRMS)[number];

export const TECH_CRM_LABELS: Record<TechCrm, string> = {
  housecallpro: "Housecall Pro",
  servicetitan: "ServiceTitan",
};

/**
 * How customer copy (screens and emails) names the software the leaderboard connects to. Say "FSM", never
 * "software". The full form goes where the term first appears; "your FSM" is fine after that.
 */
export const TECH_FSM_LABEL = `FSM (${TECH_CRM_LABELS.housecallpro} / ${TECH_CRM_LABELS.servicetitan})`;
